import type { Location } from '@mapsindoors/typescript-interfaces';
import type mapboxgl from 'mapbox-gl';
import type { MapView } from '../@types.js';

const kioskMarkerClassName = 'kiosk-pin';
const kioskMarkerAnimatedClassName = 'kiosk-pin--animated';

const createMarkerElement = (shouldAnimate: boolean) => {
  const markerElement = document.createElement('div');
  const markerImage = document.createElement('img');
  markerImage.src = 'map_pin.png';
  markerImage.className = shouldAnimate
    ? `${kioskMarkerClassName} ${kioskMarkerAnimatedClassName}`
    : kioskMarkerClassName;
  markerElement.append(markerImage);

  return markerElement;
};

type KioskMarkerOptions = {
  animate?: boolean;
};

export type SetKioskIcon = (
  mapView: MapView,
  kioskLocation: Location,
  options?: KioskMarkerOptions,
) => Promise<mapboxgl.Marker | null>;

const hasLngLatCoordinates = (
  geometry: Location['geometry'],
): geometry is Location['geometry'] & { coordinates: [number, number] } => {
  if (!geometry || typeof geometry !== 'object' || !('coordinates' in geometry)) {
    return false;
  }

  const coords = geometry.coordinates;
  return (
    Array.isArray(coords) &&
    coords.length >= 2 &&
    typeof coords[0] === 'number' &&
    typeof coords[1] === 'number'
  );
};

export const setKioskIcon: SetKioskIcon = async (mapView, location, options) => {
  const mapboxNamespace = window.mapboxgl;

  if (!mapboxNamespace) {
    console.error('Mapbox GL is not available');
    return null;
  }

  const markerElement = createMarkerElement(options?.animate === true);
  const map = mapView.getMap();
  if (!hasLngLatCoordinates(location.geometry)) {
    console.warn('Unable to determine coordinates for kiosk location');
    return null;
  }

  const [lng, lat] = location.geometry.coordinates;
  const coordinates: [number, number] = [lng, lat];

  const marker = new mapboxNamespace.Marker(markerElement).setLngLat(coordinates);

  marker.addTo(map);

  return marker;
};

export const setKioskMarkerAnimated = (
  marker: mapboxgl.Marker | null | undefined,
  animated: boolean,
) => {
  if (!marker) {
    return;
  }

  const markerRoot = marker.getElement();
  const imageEl = markerRoot.querySelector(`.${kioskMarkerClassName}`);

  if (!imageEl) {
    return;
  }

  if (animated) {
    imageEl.classList.add(kioskMarkerAnimatedClassName);
  } else {
    imageEl.classList.remove(kioskMarkerAnimatedClassName);
  }
};

export const createKioskLocationHandler = (
  setKioskLocationId: (locationId: string | null) => Promise<void>,
  setMarker: SetKioskIcon,
) => {
  let kioskMarker: mapboxgl.Marker | null = null;

  return {
    getMarker: () => kioskMarker,
    handle: async (mapView: MapView, targetKioskLocationId: string | null) => {
      kioskMarker?.remove();
      kioskMarker = null;

      if (!targetKioskLocationId) {
        await setKioskLocationId(null);
        return;
      }

      try {
        const location =
          await window.mapsindoors.services.LocationsService.getLocation(targetKioskLocationId);

        if (!location) {
          console.warn(`No location found for kioskLocationId: ${targetKioskLocationId}`);
          await setKioskLocationId(null);
          return;
        }

        await setKioskLocationId(location.id);
        kioskMarker = await setMarker(mapView, location);
      } catch (error) {
        if (error instanceof Error) {
          console.warn(error.message);
        } else {
          console.warn(error);
        }
        await setKioskLocationId(null);
      }
    },
    clear: () => {
      kioskMarker?.remove();
      kioskMarker = null;
    },
  };
};
